import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import { Plus, Copy, Trash2, CheckCircle2, Clock, Mail, XCircle, Filter, UserX } from "lucide-react";
import { toast } from "sonner";

const TIER_OPTIONS = ["TRIAL", "STANDARD", "PREMIUM"];

// Filter tabs — 'all' shows everything, others match on inv.status from backend.
const FILTER_TABS = [
  { key: "all",     label: "Alles" },
  { key: "open",    label: "Open" },
  { key: "used",    label: "Gebruikt" },
  { key: "expired", label: "Verlopen" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso) - new Date();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

export default function InvitesPage() {
  const t = useT();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [tier, setTier] = useState("STANDARD");
  const [expiryDays, setExpiryDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [bulkBusy, setBulkBusy] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invites");
      setInvites(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Per-status counts drive the filter-tab badges so the admin has instant
  // "hoeveel open, hoeveel gebruikt, hoeveel verlopen" visibility.
  const counts = useMemo(() => {
    const c = { all: invites.length, open: 0, used: 0, expired: 0 };
    for (const inv of invites) c[inv.status || "open"] = (c[inv.status || "open"] || 0) + 1;
    return c;
  }, [invites]);

  const filtered = useMemo(() => {
    if (filter === "all") return invites;
    return invites.filter((inv) => (inv.status || "open") === filter);
  }, [invites, filter]);

  const createInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        label: label.trim(),
        recipient_email: recipientEmail.trim() || null,
        recipient_name: recipientName.trim() || null,
        tier: tier || null,
        expiry_days: Number.isFinite(Number(expiryDays)) && Number(expiryDays) >= 0 ? Number(expiryDays) : 14,
      };
      const { data } = await api.post("/invites", body);
      if (data?.email_sent_at) {
        toast.success(t("Uitnodiging verstuurd naar {email}").replace("{email}", data.recipient_email));
      } else {
        toast.success(t("Nieuwe uitnodigingscode aangemaakt"));
      }
      setLabel(""); setRecipientEmail(""); setRecipientName("");
      setTier("STANDARD"); setExpiryDays(14);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  const deleteInvite = async (inv) => {
    const used = !!inv.used_by && !inv.orphaned;
    const msg = used
      ? t("Deze code is gebruikt door {email}. Het account blijft gewoon bestaan; alleen de code verdwijnt uit de lijst. Verwijderen?").replace("{email}", inv.used_email || "een gebruiker")
      : t("Deze uitnodigingscode verwijderen?");
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/invites/${inv.id}`, { params: used ? { force: true } : {} });
      toast.success(t("Verwijderd"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const orphanCount = invites.filter((inv) => inv.orphaned).length;
  const cleanupOrphans = async () => {
    if (!orphanCount) { toast.info(t("Niets te verwijderen.")); return; }
    if (!window.confirm(t("{n} weescode(s) verwijderen? (gebruikt door accounts die niet meer bestaan)").replace("{n}", orphanCount))) return;
    setBulkBusy(true);
    try {
      const { data } = await api.delete("/invites/bulk-orphaned");
      toast.success(t("{n} code(s) verwijderd").replace("{n}", data.deleted || 0));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBulkBusy(false);
  };

  const bulkCleanup = async (onlyExpired) => {
    const targetCount = onlyExpired ? counts.expired : (counts.open + counts.expired);
    if (!targetCount) {
      toast.info(t("Niets te verwijderen."));
      return;
    }
    const label = onlyExpired
      ? t("{n} verlopen code(s) verwijderen?").replace("{n}", targetCount)
      : t("{n} ongebruikte code(s) verwijderen (open + verlopen)?").replace("{n}", targetCount);
    if (!window.confirm(label)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.delete(`/invites/bulk-unused?only_expired=${onlyExpired ? "true" : "false"}`);
      toast.success(t("{n} code(s) verwijderd").replace("{n}", data.deleted || 0));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBulkBusy(false);
  };

  const copyLink = async (code) => {
    const url = `${origin}/join-the-team?code=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("Uitnodigingslink gekopieerd"));
    } catch { toast.error(t("Kopiëren mislukt — kopieer handmatig")); }
  };
  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("Code gekopieerd"));
    } catch { toast.error(t("Kopiëren mislukt — kopieer handmatig")); }
  };

  return (
    <AppLayout>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="stat-label">{t("Beheer · Toegang")}</div>
          <h1 className="font-display font-black text-5xl md:text-6xl uppercase tracking-tighter mt-1">{t("Uitnodigingen")}</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            {t("Genereer eenmalige codes en deel de link met de persoon die je wilt uitnodigen. Alleen mensen met een geldige code kunnen zich registreren.")}
          </p>
        </div>
      </div>

      <form onSubmit={createInvite} className="bg-card border-2 border-foreground p-5 heavy-shadow mb-8" data-testid="invite-create-form">
        <div className="stat-label mb-3">{t("Nieuwe uitnodiging")}</div>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className="stat-label">{t("E-mailadres ontvanger (optioneel)")}</label>
            <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder={t("peter@voorbeeld.nl")} data-testid="invite-recipient-email-input"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="stat-label">{t("Naam ontvanger (optioneel)")}</label>
            <input value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
              placeholder={t("Peter")} data-testid="invite-recipient-name-input"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="stat-label">{t("Notitie (optioneel)")}</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder={t("bv. voor Peter (penningmeester)")} data-testid="invite-label-input"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="stat-label">{t("Tier")}</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="invite-tier-select"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
              {TIER_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div>
            <label className="stat-label">{t("Verloopt na (dagen)")}</label>
            <input type="number" min="0" max="365" value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)} data-testid="invite-expiry-days-input"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary" />
            <p className="text-[10px] text-muted-foreground mt-1">{t("0 = geen expiry. Standaard 14 dagen.")}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="submit" disabled={busy} data-testid="invite-create-btn"
            className="btn-brutal bg-primary text-primary-foreground px-5 py-2 font-display uppercase font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-70">
            <Plus className="w-4 h-4" />
            {recipientEmail.trim() ? t("Verstuur uitnodiging") : t("Genereer code")}
          </button>
          <span className="text-xs text-muted-foreground">
            {recipientEmail.trim()
              ? t("Vul een e-mailadres in → er gaat automatisch een uitnodigingsmail uit.")
              : t("Laat het e-mailveld leeg om alleen een code aan te maken (deel je zelf).")}
          </span>
        </div>
      </form>

      {/* Toolbar: filter tabs + bulk-cleanup buttons */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap" data-testid="invites-toolbar">
        <div className="flex items-center gap-1 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground mr-1" />
          {FILTER_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              data-testid={`invites-filter-${tab.key}`}
              className={`btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 ${filter === tab.key ? "bg-foreground text-background" : "bg-background text-foreground"}`}>
              {t(tab.label)}
              <span className={`text-[10px] px-1 py-0.5 font-mono-num ${filter === tab.key ? "bg-background text-foreground" : "bg-muted text-muted-foreground"}`}>
                {counts[tab.key] || 0}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {orphanCount > 0 && (
            <button onClick={cleanupOrphans} disabled={bulkBusy} data-testid="invites-bulk-orphaned"
              title={t("Codes die zijn gebruikt door inmiddels verwijderde accounts")}
              className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
              <UserX className="w-3.5 h-3.5" /> {t("Weescodes opruimen ({n})").replace("{n}", orphanCount)}
            </button>
          )}
          {counts.expired > 0 && (
            <button onClick={() => bulkCleanup(true)} disabled={bulkBusy} data-testid="invites-bulk-expired"
              className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
              <XCircle className="w-3.5 h-3.5" /> {t("Verwijder verlopen ({n})").replace("{n}", counts.expired)}
            </button>
          )}
          {(counts.open + counts.expired) > 0 && (
            <button onClick={() => bulkCleanup(false)} disabled={bulkBusy} data-testid="invites-bulk-unused"
              className="btn-brutal bg-destructive text-white px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
              <Trash2 className="w-3.5 h-3.5" /> {t("Verwijder alle ongebruikte ({n})").replace("{n}", counts.open + counts.expired)}
            </button>
          )}
        </div>
      </div>

      <div className="bg-card border-2 border-foreground heavy-shadow overflow-x-auto" data-testid="invites-table">
        <div className="p-5 border-b-2 border-foreground">
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Bestaande codes")}</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">{t("Laden…")}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {filter === "all"
              ? t("Nog geen uitnodigingscodes. Genereer er hierboven eentje.")
              : t("Geen codes in deze weergave.")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-foreground text-background">
                <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Code")}</th>
                <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Ontvanger")}</th>
                <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Tier")}</th>
                <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Verloopt")}</th>
                <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Status")}</th>
                <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Acties")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <InviteRow key={inv.id} inv={inv} t={t} origin={origin}
                  onDelete={deleteInvite} onCopyLink={copyLink} onCopyCode={copyCode} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}


function InviteRow({ inv, t, origin, onDelete, onCopyLink, onCopyCode }) {
  const status = inv.status || (inv.used_by ? "used" : "open");
  const isUsed = status === "used";
  const isExpired = status === "expired";
  const isOpen = status === "open";
  const dLeft = daysUntil(inv.expires_at);

  return (
    <tr className={`${isUsed ? "bg-muted/40" : isExpired ? "bg-destructive/5" : ""}`} data-testid={`invite-row-${inv.code}`}>
      <td className="px-3 py-3 font-mono-num font-bold tracking-widest">{inv.code}</td>
      <td className="px-3 py-3 text-sm">
        {(inv.recipient_email || inv.recipient_name) ? (
          <div className="flex flex-col leading-tight">
            {inv.recipient_name && <span className="font-bold">{inv.recipient_name}</span>}
            {inv.recipient_email && (
              <span className="text-muted-foreground font-mono-num text-xs">{inv.recipient_email}</span>
            )}
            {inv.email_sent_at && (
              <span className="inline-flex items-center gap-1 text-positive text-xs mt-0.5">
                <Mail className="w-3 h-3" /> {t("Verstuurd")}
              </span>
            )}
          </div>
        ) : (<span className="text-muted-foreground">—</span>)}
        {inv.label && <div className="text-xs text-muted-foreground italic mt-1">{inv.label}</div>}
      </td>
      <td className="px-3 py-3">
        {inv.tier ? (
          <span data-testid={`invite-tier-badge-${inv.code}`}
            className="inline-flex items-center px-2 py-0.5 border-2 border-foreground bg-secondary text-secondary-foreground font-display uppercase font-bold text-xs tracking-widest">
            {inv.tier}
          </span>
        ) : (<span className="text-muted-foreground">—</span>)}
      </td>
      <td className="px-3 py-3 text-xs">
        {inv.expires_at ? (
          <div className="flex flex-col leading-tight">
            <span className="font-mono-num">{fmtDate(inv.expires_at)}</span>
            {!isUsed && dLeft != null && (
              <span className={`text-[10px] ${dLeft < 0 ? "text-destructive font-bold" : dLeft <= 3 ? "text-secondary font-bold" : "text-muted-foreground"}`}>
                {dLeft < 0
                  ? t("verlopen {n}d geleden").replace("{n}", -dLeft)
                  : dLeft === 0
                    ? t("vandaag")
                    : t("nog {n} dag(en)").replace("{n}", dLeft)}
              </span>
            )}
          </div>
        ) : (<span className="text-muted-foreground">—</span>)}
      </td>
      <td className="px-3 py-3">
        {isUsed && (
          <span className="inline-flex items-center gap-1 text-positive font-bold text-xs">
            <CheckCircle2 className="w-3.5 h-3.5" /> {t("Gebruikt")}
            {inv.used_email && <span className="font-mono-num font-normal text-muted-foreground ml-1">{inv.used_email}</span>}
          </span>
        )}
        {inv.orphaned && (
          <div className="inline-flex items-center gap-1 text-destructive text-[11px] mt-0.5" data-testid={`invite-orphaned-${inv.code}`}>
            <UserX className="w-3 h-3" /> {t("Account verwijderd — weescode")}
          </div>
        )}
        {isExpired && (
          <span className="inline-flex items-center gap-1 text-destructive font-bold text-xs">
            <XCircle className="w-3.5 h-3.5" /> {t("Verlopen")}
          </span>
        )}
        {isOpen && (
          <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
            <Clock className="w-3.5 h-3.5" /> {t("Open")}
          </span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="flex gap-2 justify-end flex-wrap">
          {isOpen && (
            <>
              <button onClick={() => onCopyLink(inv.code)} data-testid={`invite-copy-link-${inv.code}`}
                className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5"
                title={`${origin}/join-the-team?code=${inv.code}`}>
                <Copy className="w-3.5 h-3.5" /> {t("Kopieer link")}
              </button>
              <button onClick={() => onCopyCode(inv.code)} data-testid={`invite-copy-code-${inv.code}`}
                className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> {t("Alleen code")}
              </button>
            </>
          )}
          <button onClick={() => onDelete(inv)} data-testid={`invite-delete-${inv.code}`}
            title={inv.orphaned ? t("Weescode verwijderen") : isUsed ? t("Gebruikte code opschonen (account blijft bestaan)") : t("Code verwijderen")}
            className={`btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 ${isUsed && !inv.orphaned ? "bg-background text-destructive" : "bg-destructive text-white"}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
